/**
 * Окно «Загрузить» на `/results`: готовые картинки и видео MP4/MOV (выбор файла, перетаскивание, Ctrl+V) падают
 * в ленту видом «Загружено»; что не берём (формат, размер) — отсеивает `splitResultFiles` и показывает списком. Платье из «Наших фото» — по желанию (окно выбора платьев, одно), заметка — тоже.
 * Файлы уходят одним запросом по нажатию «Загрузить N»; битый файл не отменяет остальные — он приходит
 * строкой в `errors` и показывается списком. Успех → карточка первого изображения.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { DressPickerDialog } from "@/components/fitting/DressPickerDialog";
import { Dialog, DialogFooter } from "@/components/trends/Dialog";
import { btn, btnGhost, btnPrimary, ErrorLine, input } from "@/components/trends/ui";
import { RESULT_ACCEPT, splitResultFiles, VIDEO_MAX_MB } from "@/lib/resultUpload";
import { dressTitle, loadPhotos, photoKeys } from "@/lib/photos";
import { RESULT_UPLOAD_MAX, resultPath, RESULTS_PREFIX, uploadResults } from "@/lib/results";

const NO_TAKEN: readonly number[] = [];
const NO_NOTES: ReadonlyMap<number, string> = new Map();

export function UploadDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Dialog open={open} onClose={onClose} title="Загрузить результаты">
      <Form onClose={onClose} />
    </Dialog>
  );
}

/** Ключ файла для отсева повторов при добавлении (имя + размер + дата правки). */
const fileKey = (f: File) => `${f.name}|${f.size}|${f.lastModified}`;

function Form({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const client = useQueryClient();
  const [files, setFiles] = useState<File[]>([]);
  const [cut, setCut] = useState(false);
  const [done, setDone] = useState<number | null>(null);
  const [dressId, setDressId] = useState<number | null>(null);
  const [picker, setPicker] = useState(false);
  const [note, setNote] = useState("");
  const [over, setOver] = useState(false);
  const [rejected, setRejected] = useState<string[]>([]);
  // отсеяно до отправки: не тот формат или тяжелее потолка
  const [skipped, setSkipped] = useState<string[]>([]);
  const dressList = useQuery({ queryKey: photoKeys.list, queryFn: loadPhotos });
  const dress = dressList.data?.dresses.find((d) => d.id === dressId);

  const filesNow = useRef(files);
  filesNow.current = files;
  const add = (list: FileList | null | undefined) => {
    const { files: more, rejected: bad } = splitResultFiles(Array.from(list ?? []));
    setSkipped(bad);
    const have = filesNow.current;
    const seen = new Set(have.map(fileKey));
    const next = [...have, ...more.filter((f) => !seen.has(fileKey(f)))];
    setCut(next.length > RESULT_UPLOAD_MAX);
    setFiles(next.slice(0, RESULT_UPLOAD_MAX));
  };

  // Ctrl+V: картинки и видео из буфера. В поле ввода (заметка) вставка остаётся текстовой.
  const addNow = useRef(add);
  addNow.current = add;
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      const got = e.clipboardData?.files;
      if (!got || got.length === 0) return;
      e.preventDefault();
      addNow.current(got);
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, []);

  const send = useMutation({
    mutationFn: () => uploadResults(files, dressId, note),
    onSuccess: (got) => {
      void client.invalidateQueries({ queryKey: RESULTS_PREFIX });
      const first = got.variant_ids[0];
      if (first !== undefined && got.errors.length === 0) {
        onClose();
        navigate(resultPath(first));
        return;
      }
      // часть файлов отвергнута: окно остаётся, принятые уже в ленте — не грузим их второй раз
      setRejected(got.errors);
      setFiles([]);
      setCut(false);
      setDone(first ?? null);
    },
  });

  return (
    <>
      <div className="grid gap-3 p-4">
        <div
          data-annot="зона загрузки"
          className={`grid gap-1.5 rounded-[var(--radius-ui)] border border-dashed p-3 text-[12.5px] text-fg-muted ${over ? "border-accent bg-bg-elevated" : "border-border"}`}
          onDragOver={(e) => {
            if (Array.from(e.dataTransfer.types).includes("Files")) {
              e.preventDefault();
              setOver(true);
            }
          }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setOver(false);
            add(e.dataTransfer.files);
          }}
        >
          <span>
            перетащите картинки или видео MP4/MOV сюда или вставьте Ctrl+V · до {RESULT_UPLOAD_MAX} за раз, видео до{" "}
            {VIDEO_MAX_MB} МБ
          </span>
          <label className={`${btn} w-fit cursor-pointer`}>
            Выбрать файлы
            <input
              type="file"
              accept={RESULT_ACCEPT}
              multiple
              className="hidden"
              onChange={(e) => {
                add(e.target.files);
                e.target.value = "";
              }}
            />
          </label>
          {files.length > 0 ? (
            <ul className="grid gap-0.5 text-[12px] text-fg">
              {files.map((f) => (
                <li key={fileKey(f)} className="flex items-center gap-2">
                  <span className="min-w-0 truncate">{f.name || "image.png"}</span>
                  <button
                    type="button"
                    className={`${btnGhost} ml-auto`}
                    aria-label="Убрать"
                    onClick={() => {
                      setFiles((s) => s.filter((x) => x !== f));
                      setCut(false);
                    }}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          {skipped.length > 0 ? (
            <ul className="text-[12px] text-err" data-annot="не взяли">
              {skipped.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          ) : null}
          {cut ? <span className="text-err">больше {RESULT_UPLOAD_MAX} файлов за раз не берём — лишние отброшены</span> : null}
        </div>

        <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
          <span className="text-fg-muted">Платье:</span>
          <span className="text-fg">{dressId === null ? "без платья" : dress ? dressTitle(dress) : `Платье #${dressId}`}</span>
          <button type="button" className={btn} onClick={() => setPicker(true)}>
            {dressId === null ? "Выбрать" : "Сменить"}
          </button>
          {dressId !== null ? (
            <button type="button" className={btnGhost} onClick={() => setDressId(null)}>
              Без платья
            </button>
          ) : null}
        </div>

        <label className="grid gap-1 text-[12.5px] text-fg-muted">
          Заметка
          <textarea
            className={`${input} h-16 py-1`}
            value={note}
            maxLength={500}
            onChange={(e) => setNote(e.target.value)}
            placeholder="необязательно"
          />
        </label>

        <ErrorLine error={send.error} />
        {rejected.length > 0 ? (
          <ul className="text-[12px] text-err">
            {rejected.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        ) : null}
        {done !== null ? (
          <div className="flex items-center gap-2 text-[12.5px] text-fg-muted">
            Часть файлов загружена.
            <button
              type="button"
              className={btn}
              onClick={() => {
                onClose();
                navigate(resultPath(done));
              }}
            >
              Открыть загруженное
            </button>
          </div>
        ) : null}
      </div>
      <DialogFooter>
        <button type="button" className={btnGhost} onClick={onClose}>
          Отмена
        </button>
        <button type="button" className={btnPrimary} disabled={files.length === 0 || send.isPending} onClick={() => send.mutate()}>
          {send.isPending ? "Загружаю…" : `Загрузить ${files.length}`}
        </button>
      </DialogFooter>
      <DressPickerDialog
        open={picker}
        onClose={() => setPicker(false)}
        list={dressList.data}
        taken={NO_TAKEN}
        annotate={NO_NOTES}
        triedAll={null}
        single
        onAdd={(ids) => {
          const id = ids[0];
          if (id !== undefined) setDressId(id);
        }}
      />
    </>
  );
}
